import React from 'react'
import { motion } from 'framer-motion'
import { Check, MapPin } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { ArcadePageShell } from '../components/layout/ArcadePageShell'
import { NeonCity, VolcanicArena, AncientTemple, SpaceStation, Battlefield } from '../components/stages'

const stageComponents = {
  neonPier: NeonCity,
  reactorCore: VolcanicArena,
  skySpire: AncientTemple,
  ironRing: SpaceStation,
  battlefield: Battlefield,
}

const stageColors = {
  neonPier: '#a855f7',
  reactorCore: '#ef4444',
  skySpire: '#f59e0b',
  ironRing: '#06b6d4',
  battlefield: '#4facfe',
}

export function Stages() {
  const { state, dispatch, stagePresets } = useApp()

  const selectedStage = stagePresets[state.currentStageId]
  const SelectedComponent = stageComponents[state.currentStageId] || NeonCity
  const selectedColor = stageColors[state.currentStageId] || '#06b6d4'
  const stages = Object.values(stagePresets)

  const handleSelectStage = (stageId) => {
    dispatch({ type: 'SET_STAGE', payload: stageId })
  }

  return (
    <ArcadePageShell Background={SelectedComponent}>
    <div className="flex min-h-[calc(100vh-12rem)] flex-col gap-4 overflow-y-auto lg:overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex shrink-0 items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <MapPin className="text-cyan-500/50" size={22} />
          <h1 className="text-3xl font-black uppercase italic tracking-tight drop-shadow-lg">Stages</h1>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
          {stages.length} Arenas
        </span>
      </motion.div>

      <div className="flex flex-1 flex-col gap-4 lg:flex-row">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="relative min-h-[260px] flex-1 overflow-hidden rounded-xl border-2 bg-black transition-colors duration-500 lg:min-h-0"
          style={{ borderColor: `${selectedColor}66` }}
        >
          <div className="absolute inset-0">
            <SelectedComponent className="h-full w-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div
            className="absolute left-0 top-0 h-1 w-full transition-colors duration-500"
            style={{ backgroundColor: selectedColor, boxShadow: `0 0 20px ${selectedColor}` }}
          />
          <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
            <motion.div key={selectedStage.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="text-3xl font-black uppercase italic drop-shadow-lg lg:text-4xl" style={{ color: selectedColor }}>
                {selectedStage.name}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-300">{selectedStage.description}</p>
              <span className="mt-3 inline-flex rounded-full border border-white/10 bg-black/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gray-300">
                {selectedStage.label}
              </span>
            </motion.div>
          </div>
        </motion.div>

        <div className="flex shrink-0 gap-2 overflow-x-auto pb-2 lg:w-72 lg:flex-col lg:overflow-y-auto lg:pb-0">
          {stages.map((stage, index) => {
            const isSelected = state.currentStageId === stage.id
            const StageComponent = stageComponents[stage.id] || NeonCity
            const color = stageColors[stage.id] || '#06b6d4'

            return (
              <motion.button
                key={stage.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                onClick={() => handleSelectStage(stage.id)}
                className={`group relative aspect-video w-40 shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-300 lg:w-full lg:aspect-[3/1] ${isSelected ? 'scale-[1.02]' : 'border-white/10 opacity-60 hover:border-white/30 hover:opacity-100'}`}
                style={isSelected ? { borderColor: color, boxShadow: `0 0 15px ${color}55` } : {}}
              >
                <div className="absolute inset-0">
                  <StageComponent className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className={`absolute inset-0 transition-colors duration-300 ${isSelected ? 'bg-black/20' : 'bg-black/45'}`} />
                </div>
                <div className="relative z-10 flex h-full items-center justify-between px-3">
                  <span className="text-xs font-black uppercase tracking-wider drop-shadow-lg">{stage.name}</span>
                  {isSelected && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: color }}>
                      <Check size={12} className="text-black" />
                    </span>
                  )}
                </div>
              </motion.button>
            )
          })}
        </div>
      </div>
    </div>
    </ArcadePageShell>
  )
}
